import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AppleSignInButton from "@/components/AppleSignInButton";
import DisclaimerNote from "@/components/DisclaimerNote";

const TIER_LABEL = {
  community: "Community ($1/mo)",
  pro: "Pro ($2,500/mo)",
  pro_plus: "Pro Plus ($4,500/mo)",
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tier = params.get("tier");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { setErr("You must accept the Terms of Service and Privacy Policy."); return; }
    setBusy(true); setErr("");
    try {
      await register({ ...form, terms_accepted: true });
      toast.success("Account created");
      if (tier && ["community", "pro", "pro_plus"].includes(tier)) {
        // Forward the tier choice so Billing.jsx can auto-fire checkout on land.
        nav(`/app/billing?auto_checkout=checkmate_${tier}`);
      } else {
        nav("/app");
      }
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grid-noise pointer-events-none" />
      <div className="w-full max-w-md hard-card hard-card-shadow p-8 relative">
        <Link to="/" className="overline mb-8 inline-block hover:underline">← CHECKMATE</Link>
        <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Create Account</h1>
        <p className="text-sm text-muted-foreground mb-2">Start your own payroll workspace.</p>
        {tier && TIER_LABEL[tier] && (
          <div className="border-2 border-good bg-good/10 text-good p-3 font-mono text-xs mb-4" data-testid="register-tier-note">
            Signing up for <b>{TIER_LABEL[tier]}</b>. You'll finish checkout on the next page.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="overline mb-2">Your Name</div>
            <input data-testid="register-name" required value={form.name} onChange={update("name")} className="w-full px-4 py-3 border-2 border-ink font-mono text-sm" />
          </div>
          <div>
            <div className="overline mb-2">Company Name</div>
            <input data-testid="register-company" required value={form.company_name} onChange={update("company_name")} className="w-full px-4 py-3 border-2 border-ink font-mono text-sm" />
          </div>
          <div>
            <div className="overline mb-2">Work Email</div>
            <input data-testid="register-email" type="email" required value={form.email} onChange={update("email")} className="w-full px-4 py-3 border-2 border-ink font-mono text-sm" />
          </div>
          <div>
            <div className="overline mb-2">Password (min 8)</div>
            <input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={update("password")} className="w-full px-4 py-3 border-2 border-ink font-mono text-sm" />
          </div>
          <label className="flex items-start gap-3 border-2 border-ink p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="register-terms-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="w-5 h-5 border-2 border-ink mt-0.5 shrink-0"
            />
            <span className="font-mono text-[11px] leading-relaxed">
              I agree to the{" "}
              <Link to="/legal/terms" target="_blank" className="underline">Terms of Service</Link> and{" "}
              <Link to="/legal/privacy" target="_blank" className="underline">Privacy Policy</Link>, including the
              Dual-Fuel funding responsibility and the 7-year{" "}
              <Link to="/legal/retention" target="_blank" className="underline">data retention</Link> schedule.
            </span>
          </label>
          {err && <div data-testid="register-error" className="border-2 border-bad text-bad font-mono text-xs p-3">{err}</div>}
          <button data-testid="register-submit" disabled={busy || !agreed} className="hard-btn w-full py-3 font-mono uppercase tracking-widest text-sm disabled:opacity-60">{busy ? "Creating…" : "Create Account →"}</button>
        </form>
        <div className="mt-6">
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-ink/20" /></div>
            <div className="relative text-center"><span className="bg-paper px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</span></div>
          </div>
          <AppleSignInButton label="Sign up with Apple" />
        </div>
        <div className="mt-6 pt-6 border-t-2 border-ink text-sm">
          <Link data-testid="register-to-login" to="/login" className="underline underline-offset-4">← Already have an account? Sign in</Link>
        </div>
        <DisclaimerNote />
      </div>
    </div>
  );
}
